import { brands } from '@trmediaab/zebra-utils';
import { useMemo } from 'react';

import type { CheckoutState } from '../state';
import { useImmutableState } from '../state';
import { getProductLineItems, getProductSite } from './orderUtils';

type DataLayerObject = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer: DataLayerObject[];
  }
}

function addDataLayerEvent(obj: DataLayerObject) {
  window.parent.dataLayer = window.parent.dataLayer || [];
  window.parent.dataLayer.push(obj);
}

export function useAnalytics() {
  const { extraAnalytics } = useImmutableState();

  const analytics = useMemo(
    () => ({
      beginCheckout: (order: NonNullable<CheckoutState['order']>) => {
        try {
          const site = getProductSite(order);
          const brand = site != null ? brands.getBrand(site) : null;
          const lineItems = getProductLineItems(order);

          if (lineItems == null) {
            throw new Error(`Order ${order.id} has no line items`);
          }

          const eventObj = {
            event: 'begin_checkout',
            ecommerce: {
              currency: order.organization === 'trmedia_ab' ? 'SEK' : 'NOK',
              value: order.totalPrice / 100,
              items: lineItems.map((lineItem, idx) => ({
                item_id: lineItem.product.sku,
                item_name: lineItem.product.headline,
                affiliation: brand != null ? brand.name : undefined,
                coupon: order.campaign?.code,
                discount:
                  lineItem.discount != null ? lineItem.discount / 100 : 0,
                lineItem: idx,
                item_category: lineItem.product.type,
                price: lineItem.product.price / 100,
                quantity: lineItem.quantity,
                ...extraAnalytics,
              })),
            },
          };
          addDataLayerEvent(eventObj);
        } catch (error) {
          console.error(`Failed event 'begin_checkout', got ${error}`);
        }
      },
      purchase: (order: NonNullable<CheckoutState['order']>) => {
        try {
          const site = getProductSite(order);
          const brand = site != null ? brands.getBrand(site) : null;
          const lineItems = getProductLineItems(order);

          if (lineItems == null) {
            throw new Error(`Order ${order.id} has no line items`);
          }

          const eventObj = {
            event: 'purchase',
            ecommerce: {
              email: order.email,
              currency: order.organization === 'trmedia_ab' ? 'SEK' : 'NOK',
              value: order.totalPrice / 100,
              tax: order.totalVAT / 100,
              transaction_id: order.id,
              items: lineItems.map((lineItem, idx) => ({
                item_id: lineItem.product.sku,
                item_name: lineItem.product.headline,
                affiliation: brand != null ? brand.name : undefined,
                coupon: order.campaign?.code,
                discount:
                  lineItem.discount != null ? lineItem.discount / 100 : 0,
                lineItem: idx,
                item_category: lineItem.product.type,
                price: lineItem.product.price / 100,
                quantity: lineItem.quantity,
                ...extraAnalytics,
              })),
            },
          };
          addDataLayerEvent(eventObj);
        } catch (error) {
          console.error(`Failed event 'purchase', got ${error}`);
        }
      },
    }),
    [extraAnalytics],
  );

  return analytics;
}
